import React from 'react';
import { BasketballScore, BasketballStatus } from '../../types';

interface Props { score: BasketballScore; homeTeam: string; awayTeam: string; }

const STATUS_META: Record<BasketballStatus, { label: string; color: string; dot?: boolean }> = {
  not_started:    { label: 'Pre-Game',    color: '#4A5568' },
  first_quarter:  { label: '1st Quarter', color: '#F97316', dot: true },
  second_quarter: { label: '2nd Quarter', color: '#F97316', dot: true },
  halftime:       { label: 'Half Time',   color: '#EAB308' },
  third_quarter:  { label: '3rd Quarter', color: '#F97316', dot: true },
  fourth_quarter: { label: '4th Quarter', color: '#F97316', dot: true },
  overtime:       { label: 'Overtime',    color: '#A78BFA', dot: true },
  final:          { label: 'Final',       color: '#22C55E' },
};

const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4', 'OT'];

const BasketballBoxScore: React.FC<Props> = ({ score, homeTeam, awayTeam }) => {
  const meta = STATUS_META[score.status] ?? { label: score.status, color: '#8892A4' };
  const currentQ = Math.min(score.quarter ?? 1, 5);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Status badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
        {meta.dot && (
          <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            background: meta.color, boxShadow: `0 0 6px ${meta.color}`,
            animation: 'pulse 1.4s ease-in-out infinite',
          }} />
        )}
        <span style={{ fontSize: '12px', fontWeight: 700, color: meta.color, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          {meta.label}
        </span>
      </div>

      {/* Main scoreboard */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', overflow: 'hidden',
      }}>
        {/* Quarter header row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr repeat(5, 36px) 48px',
          padding: '6px 12px', borderBottom: '1px solid var(--hairline)',
          fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase',
        }}>
          <div>Team</div>
          {QUARTER_LABELS.map((q, i) => (
            <div key={q} style={{
              textAlign: 'center',
              color: i + 1 === currentQ && score.status !== 'not_started' && score.status !== 'final' ? '#F97316' : 'var(--ink-4)',
            }}>
              {q}
            </div>
          ))}
          <div style={{ textAlign: 'center', color: 'var(--ink-1)' }}>Total</div>
        </div>

        {/* Away row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr repeat(5, 36px) 48px',
          padding: '10px 12px', borderBottom: '1px solid var(--hairline)', alignItems: 'center',
        }}>
          <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--ink-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {awayTeam}
          </div>
          {QUARTER_LABELS.map((_, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: '13px', color: 'var(--ink-3)' }}>
              {score.status === 'not_started' || i + 1 > currentQ ? (i + 1 < currentQ ? '0' : '—') : '—'}
            </div>
          ))}
          <div style={{ textAlign: 'center', fontSize: '22px', fontWeight: 900, color: score.away_score > score.home_score ? '#22C55E' : 'var(--ink-1)' }}>
            {score.away_score}
          </div>
        </div>

        {/* Home row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr repeat(5, 36px) 48px',
          padding: '10px 12px', alignItems: 'center',
        }}>
          <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--ink-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {homeTeam}
          </div>
          {QUARTER_LABELS.map((_, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: '13px', color: 'var(--ink-3)' }}>
              {score.status === 'not_started' || i + 1 > currentQ ? (i + 1 < currentQ ? '0' : '—') : '—'}
            </div>
          ))}
          <div style={{ textAlign: 'center', fontSize: '22px', fontWeight: 900, color: score.home_score > score.away_score ? '#22C55E' : 'var(--ink-1)' }}>
            {score.home_score}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasketballBoxScore;
